import { useState } from "react";
import { App } from "antd";
import { exportItem } from "@/api/reports";
import {
  downloadFile,
  parseCSVFileName,
  extractCSVContent,
} from "@/utils/downloadFile";

/**
 * useReportExport — экспорт отчёта в Excel/CSV и скачивание файла.
 *
 * Параметры:
 *  - reportId: uuid отчёта
 *  - reportName: имя отчёта (используется как fallback в имени файла)
 *
 * Возвращает:
 *  - pending: индикатор процесса экспорта
 *  - exportReport(type, filters): экспорт в 'excel' | 'csv' с учётом фильтров
 */
export const useReportExport = (reportId, reportName) => {
  const { message } = App.useApp();
  const [pending, setPending] = useState(false);

  // Экспорт в Excel (XLSX): получаем Blob, формируем имя и скачиваем
  const exportExcel = async (filters) => {
    const response = await exportItem({
      uuid: reportId,
      type: "excel",
      filters,
    });

    if (!response.ok) throw new Error("Не удалось экспортировать отчет");

    const blob = await response.blob();
    const contentDisposition = response.headers.get("Content-Disposition");

    let fileName = `${reportName || "report"}.xlsx`;
    if (contentDisposition) {
      const match = /filename="?([^"]+)"?/.exec(contentDisposition);
      if (match?.[1]) fileName = decodeURIComponent(match[1]);
    }

    downloadFile(blob, fileName);
  };

  // Экспорт в CSV: нормализуем контент, подставляем BOM для корректной кодировки
  const exportCSV = async (filters) => {
    const response = await exportItem({ uuid: reportId, type: "csv", filters });

    if (!response.ok) throw new Error("Не удалось экспортировать отчет");

    const text = await response.text();
    const { reportTitle, csvContent } = extractCSVContent(text);

    const csvBlob = new Blob(["\uFEFF" + csvContent], {
      type: "text/csv;charset=utf-8;",
    });

    const contentDisposition = response.headers.get("Content-Disposition");
    const fileName = parseCSVFileName(
      contentDisposition,
      reportTitle || reportName || "report"
    );

    downloadFile(csvBlob, fileName);
  };

  // Единая точка входа экспорта по типу
  const exportReport = async (type, filters) => {
    setPending(true);
    try {
      if (type === "excel") {
        await exportExcel(filters);
      } else {
        await exportCSV(filters);
      }
    } catch (error) {
      message.error(error?.message || "Ошибка экспорта");
    } finally {
      setPending(false);
    }
  };

  return { pending, exportReport };
};
